import { createPortal } from 'react-dom';
import { useEffect, useMemo } from 'react';
import { AnimatePresence } from 'motion/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { useLLMAgent, useLlmEditorScope } from '@/hooks/llm';
import {
  useReviewActions,
  toPlainPendingReview,
  type PendingReview,
  type AgentEditTarget,
  type PersistedDocumentWriter,
} from '@/hooks/llm/use-llm-agent';
import { useDocumentId } from '@/hooks/use-document-id';
import { useDraftParams } from '@/hooks/use-draft-params';
import { useNote, useNoteWriter } from '@/hooks/rxdb-models';
import { useNavigateWithSearch } from '@/hooks/use-navigate-with-search';
import { guardOwnWrite } from '@/hooks/rxdb-base';
import { useAccessToken } from '@flintmint/web-shared/hooks/use-auth-user';

import { LlmChat } from '@/features/llm-chat';
import { BackToNote } from '@/components/back-to-note';
import { SuggestionsNavigation } from '@/components/suggestion-navigation';

import { appRoutes } from '@/constants/routes';
import { chatbotOpenAtom, editorAtom } from '@/state';
import { isEditorAlive } from '@/utils/editor-utils';

interface NotePromptBoxProps {
  hideLlmChat?: boolean;
}

export const NotePromptBox = ({ hideLlmChat }: NotePromptBoxProps) => {
  const editor = useAtomValue(editorAtom);

  const setChatOpen = useSetAtom(chatbotOpenAtom);

  const [searchParams] = useSearchParams();
  const { id, isDraft } = useDocumentId();
  const { draftType } = useDraftParams();
  const domain = searchParams.get('domain');

  const activeDocumentId = isDraft ? 'new' : id;

  const llmRequestId = id ?? 'prompt-box';

  const { data: note } = useNote(activeDocumentId ?? '');
  const { mutate: upsertNote } = useNoteWriter(note, id, { variant: draftType, createIfMissing: isDraft });

  const navigate = useNavigate();
  const location = useLocation();
  const navigateWithSearch = useNavigateWithSearch();

  const accessToken = useAccessToken();
  // Unguarded write, recorded so the editor host's autosave can guard with its stamp — see guardOwnWrite.
  const persistNoteDocumentPatch: PersistedDocumentWriter = async (patch) => {
    await guardOwnWrite(note, () => upsertNote(patch));
  };

  const {
    chatMessages,
    sendChatMessage,
    retryLastMessage,
    handleSuggestionDecision,
    cancel: cancelAgentRequest,
  } = useLLMAgent({
    requestId: llmRequestId,
    editor,
    document: note,
    persistDocumentPatch: persistNoteDocumentPatch,
  });

  const { activeBlock, hasSelection } = useLlmEditorScope(editor);

  const documentPendingReview = (note?.pendingReview ?? null) as PendingReview | null;
  const pendingReview = useMemo(
    () => (documentPendingReview ? toPlainPendingReview(documentPendingReview) : null),
    [documentPendingReview]
  );
  const hasPendingReview = (pendingReview?.suggestions.length ?? 0) > 0;
  const review = useReviewActions({
    requestId: llmRequestId,
    editor,
    pendingReview,
    persistDocumentPatch: persistNoteDocumentPatch,
    document: note,
  });

  useEffect(() => {
    if (!accessToken || !id || !note || !isEditorAlive(editor) || isDraft) return;

    const prompt = location.state?.shorthandNote ?? location.state?.pendingNudge;
    if (!prompt) return;

    navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
    setChatOpen(true);
    sendChatMessage(prompt);
  }, [id, accessToken, note, editor]);

  useEffect(() => {
    if (id && !isDraft && domain) {
      const params = new URLSearchParams(location.search);
      params.delete('domain');

      navigate(
        {
          pathname: location.pathname,
          search: params.toString() ? `?${params.toString()}` : '',
        },
        { replace: true }
      );
    }
  }, [id, isDraft, domain, location.pathname, location.search, navigate]);

  const nudgeNewNote = async (userPrompt: string) => {
    try {
      const newNote = await upsertNote({
        variant: draftType,
      });

      navigateWithSearch(`${appRoutes.library}/${newNote.id}`, {
        replace: true,
        state: { pendingNudge: userPrompt },
      });
    } catch (error) {
      console.error('Failed to create new note:', error);
    }
  };

  const handleChatPrompt = (userPrompt: string, options?: { editTarget?: AgentEditTarget }) => {
    if (!activeDocumentId || activeDocumentId === 'new') {
      return nudgeNewNote(userPrompt);
    }
    // A saved note that hasn't synced yet has no editor content; the agent would work from a blank
    // document and its persist would overwrite the real note.
    if (!note) return;

    void sendChatMessage(userPrompt, options);
  };

  return (
    <>
      {!hasPendingReview && <BackToNote />}
      {hasPendingReview &&
        editor &&
        review &&
        createPortal(
          <SuggestionsNavigation editor={editor} review={review} onAllResolved={handleSuggestionDecision} />,
          document.querySelector('main') ?? document.body
        )}
      <AnimatePresence>
        {!hideLlmChat && (
          <LlmChat
            key="note-chat"
            editor={editor}
            requestId={llmRequestId}
            activeBlock={activeBlock}
            chatMessages={chatMessages}
            hasSelection={hasSelection}
            onSubmitPrompt={handleChatPrompt}
            onRetryNetworkError={retryLastMessage}
            cancelAgentRequest={cancelAgentRequest}
          />
        )}
      </AnimatePresence>
    </>
  );
};
